import { CancelledError, QueryClient } from '@tanstack/react-query';
import { redirect, type MiddlewareFunction } from 'react-router';

import type { SessionPlQueryReturnData } from '~/services/ClientServiceTS';
import {
  getSessionPlQueryOptions,
  SESSION_PL_MIDDLEWARE_STALE_TIME_MS,
} from './sessionPlQueryOptions';
import { sessionContext } from './sessionContext';
import { perms } from './perms';

function getNoSessionRedirect(request: Request) {
  const url = new URL(request.url);
  const noSessionReferer =
    url.pathname !== '/no-session'
      ? new URLSearchParams({ referer: url.pathname + url.search }).toString()
      : '';
  return redirect(`/no-session?${noSessionReferer}`);
}

function hasAccountScopedSessionCache(queryClient: QueryClient) {
  const entries = queryClient.getQueriesData<SessionPlQueryReturnData>({
    queryKey: ['session.pl'],
  });
  return entries.some(([key, data]) => !!data && key[1] != null && key[2] != null);
}

/**
 * Staff layout middleware: resolves session.pl (no account params; network
 * when stale) and requires a Dianomi login before child loaders run.
 */
export function resolveStaffSessionMiddleware(queryClient: QueryClient): MiddlewareFunction {
  return async ({ request, context }, next) => {
    try {
      if (hasAccountScopedSessionCache(queryClient)) {
        await queryClient.cancelQueries({ queryKey: ['session.pl'] });
      }

      const sessionData = await queryClient.fetchQuery({
        ...getSessionPlQueryOptions({}),
        staleTime: SESSION_PL_MIDDLEWARE_STALE_TIME_MS,
      });

      if (!perms.isDianomi({ sessionPlData: sessionData })) {
        throw redirect('/forbidden');
      }

      context.set(sessionContext, sessionData);
    } catch (e) {
      if (e instanceof Response) {
        throw e;
      }
      console.error(e);
      if (e instanceof CancelledError) {
        throw e;
      }
      throw getNoSessionRedirect(request);
    }

    return next();
  };
}
